import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ITokenValid } from '@pacifico/interfaces/components.interface';
import { IRequestClientDoubleFactor } from '@pacifico/interfaces/shared-api.interface';
import { SharedApiService } from '@pacifico/services/api/shared/shared-api.service';
import { TokenService } from '@pacifico/services/api/token.service';
import { AlertService } from '@pacifico/services/local/alert.service';
import { SHIPPING_WAY } from '@pacifico/utils/enums';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
	selector: 'app-valid-token',
	templateUrl: './valid-token.component.html',
	styleUrls: ['./valid-token.component.scss']
})
export class ValidTokenComponent implements OnInit, OnChanges, OnDestroy {
	@Output() token = new EventEmitter<ITokenValid>();
	/**
	 **Cuando es coordenada se cumple, el tokenType = 2
	 *Cuando es sms o email se cumple que el el tokenType = 1
	 */
	@Output() tokenType = new EventEmitter<string>();
	@Input() operationCode: string | undefined;
	@Input() clear = false;

	formArray: FormArray;

	constructor(
		private _formBuilder: FormBuilder,
		private _sharedApiService: SharedApiService,
		private _tokenService: TokenService,
		private _alertService: AlertService
	) {
		this.formArray = this._formBuilder.array([]);
		for (let index = 0; index < 4; index++) {
			this.formArray.insert(index, new FormControl('', Validators.required));
		}
		this.subscriptionValue();
	}

	ngOnChanges(): void {
		if (this.clear) {
			this.formArray.reset();
		}
	}

	ngOnInit(): void {
		this._requestDefaultDoubleFactor();

		this.phone = this.maskCharacter(this.phone, '*', 3);
		const characterEmail = this.email.substr(this.email.indexOf('@'), this.email.length - 1);
		this.email = this.maskCharacter(this.email, '*', characterEmail.length, 2);
	}

	private subscription!: Subscription;
	shippingWay = this._tokenService.getProfileAccountUser().preferences;
	email = this._tokenService.getProfileAccountUser().email;
	phone = `${this._tokenService.getProfileAccountUser().mobile}`;
	cardCoordinate = this._tokenService.getProfileAccountUser().cardCoordinate;
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	keyDown(control: AbstractControl, event: KeyboardEvent): void {
		const element = event.target as HTMLInputElement;

		if (event.key === 'Backspace') {
			this._nextPreviuElement(control, element, false);
		} else {
			const rex = new RegExp('^[0-9]*$');
			if (rex.test(event.key) === false) {
				return;
			}
			this._nextPreviuElement(control, element, true);
		}
	}

	clickValidate(): void {
		this._validate();
	}

	clickResend(): void {
		this._requestDefaultDoubleFactor();
	}

	private _requestDefaultDoubleFactor(): void {
		const requestClient: IRequestClientDoubleFactor = {
			operationCode: this._getOperationCode(),
			personId: this._tokenService.getUser(),
			shippingDetails: this._tokenService.getShippingDetailUser(),
			shippingWay: this._tokenService.getProfileAccountUser().preferences
		};

		this._sendToken(requestClient);
	}

	private subscriptionValue(): void {
		this.subscription = this.formArray.valueChanges.pipe(debounceTime(500)).subscribe((data) => {
			if (data) {
				const item = `${this.formArray.at(0).value as string} ${this.formArray.at(1).value as string} ${
					this.formArray.at(2).value as string
				} ${this.formArray.at(3).value as string}`;

				const tokenValid = item.replace(/\s/g, '');
				this.token.emit({ token: tokenValid, valid: this.formArray.valid });
			}
		});
	}

	private _nextPreviuElement(control: AbstractControl, element: HTMLInputElement, isNext: boolean): void {
		const value = control.value as string;
		const elementSibling = isNext ? element.nextElementSibling : element.previousElementSibling;
		const elementInput = elementSibling as HTMLInputElement;

		if (value) {
			control.setValue('');
		}

		if (elementInput == null) {
			return;
		}

		if (!isNext) {
			control.reset();
		}

		setTimeout(() => {
			elementInput.focus();
		}, 250);
	}

	private _validate(): void {
		const requestClient: Partial<IRequestClientDoubleFactor> = {
			operationCode: this._getOperationCode(),
			personId: this._tokenService.getUser()
		};
		// si el shippingWay actual es mobile o es coordenada entonces quiere decir que se enviara el token al email
		if (this.shippingWay === SHIPPING_WAY.MOBILE || this.shippingWay === SHIPPING_WAY.COORDINATE) {
			requestClient.shippingDetails = this._tokenService.getProfileAccountUser().email;
			requestClient.shippingWay = SHIPPING_WAY.EMAIL;
		} else {
			requestClient.shippingDetails = `${this._tokenService.getProfileAccountUser().mobile}`;
			requestClient.shippingWay = SHIPPING_WAY.MOBILE;
		}
		this._sendToken(requestClient as IRequestClientDoubleFactor);
	}

	private _getOperationCode(): string {
		let operationCode = this.operationCode;
		if (!operationCode) {
			operationCode = this._tokenService.getProfileAccountUser().operationCode;
		}
		return operationCode;
	}

	maskCharacter(text: string, mask: string, showCount = 1, showCountInit?: number): string {
		if (showCountInit) {
			return (
				('' + text).slice(0, showCountInit) +
				('' + text).slice(showCountInit, -showCount).replace(/./g, mask) +
				('' + text).slice(-showCount)
			);
		}

		return ('' + text).slice(0, -showCount).replace(/./g, mask) + ('' + text).slice(-showCount);
	}

	private _sendToken(requestClient: IRequestClientDoubleFactor): void {
		this._sharedApiService.clientDoubleFactor(requestClient).subscribe((response) => {
			if (response) {
				if (response.status.code === '0000' && response.data.code !== '0000') {
					if (response.data.message === 'null' || response.data.message === null) {
						this._alertService.showError('Ocurrio un error inesperado, vuelve a intentar');
						return;
					}

					this._alertService.showError(response.data.message);
					return;
				}

				if (response.status.code === '0000' && response.data.code === '0000') {
					this.tokenType.emit(this._getTokenType(requestClient.shippingWay));
				}

				if (
					response.status.code === '0000' &&
					response.data.code === '0000' &&
					this.shippingWay === SHIPPING_WAY.COORDINATE &&
					response.data.coordinates
				) {
					this.cardCoordinate = response.data.coordinates;
				}
			} else {
				this._alertService.showError('Ocurrio un error inesperado, vuelve a intentar');
			}
		});
	}

	private _getTokenType(shippingWay: string): string {
		return shippingWay;
		if (shippingWay === SHIPPING_WAY.COORDINATE) {
			return '2';
		}

		return '1';
	}

	validateShowSendPhone(): boolean {
		// dont show if shippingway is phone
		return this.shippingWay === '1' || this.shippingWay === '3';
	}
}
